import { SxProps, Theme } from "@mui/material";

export const modalBoxStyles: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "16px",
  boxShadow: 24,
  background: "none",
  width: { xs: "92%", sm: "90%", md: "720px" },
  maxWidth: { xs: "520px", sm: "820px" },
  outline: "none",
};

export const headerStyles: SxProps<Theme> = (theme: Theme) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: theme.palette.recordingsModal.headerBg,
  paddingY: "16px",
  paddingX: "38px",
  borderTopLeftRadius: "16px",
  borderTopRightRadius: "16px",
});

export const contentStyles: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.recordingsModal.background,
  paddingY: "22px",
  paddingX: { xs: "20px", sm: "38px" },
  borderBottomLeftRadius: "16px",
  borderBottomRightRadius: "16px",
  overflow: "auto",
  maxHeight: "70dvh",
});

export const itemsGridStyles = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
  gap: "12px",
  width: "100%",
  alignItems: "stretch",
};

export const fullWidthGridItemStyles = {
  gridColumn: { xs: "1", sm: "1 / -1" },
};

export const loadingGridItemStyles = {
  gridColumn: { xs: "1", sm: "1 / -1" },
  display: "flex",
  justifyContent: "center",
  py: 3,
};

export const itemStyles: SxProps<Theme> = (theme: Theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  padding: "14px 16px",
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  minWidth: 0,
  height: "100%",
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  transition: "box-shadow 0.16s ease, border-color 0.16s ease",
  cursor: "pointer",
  "&:hover": {
    boxShadow: theme.shadows[2],
    borderColor: theme.palette.primary.main,
  },
});

export const detailHeaderRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  mb: 2,
};

export const backButtonStyles: SxProps<Theme> = (theme: Theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  border: "none",
  background: "none",
  padding: 0,
  marginTop: "2px",
  cursor: "pointer",
  color: theme.palette.modalColors.colorLable,
  fontFamily: "Red Hat Display",
  fontSize: "14px",
  fontWeight: 600,
  flexShrink: 0,
  "&:hover": {
    opacity: 0.8,
  },
});

export const detailSummaryStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  minWidth: 0,
  flex: 1,
};

export const sectionTitleStyles: SxProps<Theme> = (theme: Theme) => ({
  fontFamily: "Red Hat Display",
  fontSize: "14px",
  fontWeight: 700,
  color: theme.palette.modalColors.colorLable,
  mb: 1,
});

export const competencyListStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  mb: 2.5,
};

export const competencyRowStyles = (
  needsReview: boolean,
): SxProps<Theme> => (theme) => {
  if (needsReview) {
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      padding: "10px 12px",
      borderRadius: "10px",
      border: "1px solid rgba(221, 80, 62, 0.35)",
      backgroundColor: "rgba(221, 80, 62, 0.08)",
    };
  }

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid #66EF64B2",
    backgroundColor: "#66EF6433",
  };
};

export const competencyNameStyles = (
  isStrong = false,
): SxProps<Theme> => (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    fontFamily: "Red Hat Display",
    fontSize: "13px",
    fontWeight: 600,
    color:
      isStrong && isDark
        ? "#FFFFFF"
        : theme.palette.modalColors.colorLable,
  };
};

export const competencyStatsStyles = (
  isStrong = false,
): SxProps<Theme> => (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    fontFamily: "Red Hat Display",
    fontSize: "12px",
    fontWeight: 500,
    color:
      isStrong && isDark
        ? "#FFFFFF"
        : theme.palette.modalColors.colorLable,
    opacity: isStrong && isDark ? 0.9 : 0.75,
    flexShrink: 0,
  };
};

export const questionsStackStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

export const questionCardStyles: SxProps<Theme> = (theme: Theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  padding: "14px 16px",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
});

export const questionLabelRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  flexWrap: "wrap",
};

export const questionIndexStyles: SxProps<Theme> = (theme: Theme) => ({
  fontFamily: "Red Hat Display",
  fontSize: "12px",
  fontWeight: 700,
  color: theme.palette.modalColors.colorLable,
  opacity: 0.7,
});

export const competencyChipStyles: SxProps<Theme> = {
  fontFamily: "Red Hat Display",
  fontSize: "11px",
  fontWeight: 700,
  color: "#06B6D4",
  backgroundColor: "rgba(6, 182, 212, 0.12)",
  borderRadius: "999px",
  px: 1.25,
  py: 0.35,
};

export const questionTextStyles: SxProps<Theme> = (theme: Theme) => ({
  fontFamily: "Red Hat Display",
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.modalColors.colorLable,
  lineHeight: 1.45,
});

export const scoreBadgeStyles = (
  isCorrect: boolean,
): SxProps<Theme> => (theme) => {
  const isDark = theme.palette.mode === "dark";

  if (isDark) {
    return {
      fontFamily: "Red Hat Display",
      fontSize: "12px",
      fontWeight: 700,
      color: isCorrect ? "#86EFAC" : "#FECACA",
      backgroundColor: isCorrect
        ? "rgba(34, 197, 94, 0.28)"
        : "rgba(239, 68, 68, 0.32)",
      border: isCorrect
        ? "1px solid rgba(74, 222, 128, 0.55)"
        : "1px solid rgba(248, 113, 113, 0.55)",
      borderRadius: "8px",
      px: 1,
      py: 0.35,
      flexShrink: 0,
    };
  }

  return {
    fontFamily: "Red Hat Display",
    fontSize: "12px",
    fontWeight: 700,
    color: isCorrect ? "#15803D" : "#B91C1C",
    backgroundColor: isCorrect
      ? "rgba(34, 197, 94, 0.15)"
      : "rgba(239, 68, 68, 0.12)",
    border: isCorrect
      ? "1px solid rgba(34, 197, 94, 0.35)"
      : "1px solid rgba(239, 68, 68, 0.35)",
    borderRadius: "8px",
    px: 1,
    py: 0.35,
    flexShrink: 0,
  };
};

export const chosenAnswerLabelStyles = (
  variant: "chosen-correct" | "chosen-wrong",
): SxProps<Theme> => (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    display: "block",
    fontFamily: "Red Hat Display",
    fontSize: "11px",
    fontWeight: 700,
    color:
      variant === "chosen-wrong" || isDark
        ? "#FFFFFF"
        : "#047857",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    mb: 0.5,
    opacity: variant === "chosen-correct" && isDark ? 0.95 : 1,
  };
};

export const answerOptionStyles = (
  variant: "chosen-correct" | "chosen-wrong" | "correct-other" | "neutral",
): SxProps<Theme> => (theme) => {
  const isDark = theme.palette.mode === "dark";
  const primaryTextColor = isDark
    ? "#FFFFFF"
    : theme.palette.modalColors.colorLable;

  const baseOption = {
    padding: "10px 12px",
    borderRadius: "8px",
    fontFamily: "Red Hat Display",
    fontSize: "13px",
    fontWeight: 500,
  };

  if (variant === "chosen-correct") {
    return {
      ...baseOption,
      backgroundColor: "#66EF64B2",
      border: "2px solid #06B6D4",
      color: primaryTextColor,
      fontWeight: 600,
    };
  }
  if (variant === "chosen-wrong") {
    return {
      ...baseOption,
      backgroundColor: "#DD503E",
      border: "2px solid #DD503E",
      color: "#FFFFFF",
      fontWeight: 600,
    };
  }
  if (variant === "correct-other") {
    return {
      ...baseOption,
      backgroundColor: "#66EF6433",
      border: "1px solid #66EF64B2",
      color: primaryTextColor,
    };
  }
  return {
    ...baseOption,
    backgroundColor: isDark
      ? "rgba(148, 163, 184, 0.18)"
      : "rgba(148, 163, 184, 0.12)",
    border: isDark
      ? "1px solid rgba(148, 163, 184, 0.35)"
      : "1px solid rgba(148, 163, 184, 0.25)",
    color: primaryTextColor,
  };
};

export const openAnswerBoxStyles: SxProps<Theme> = (theme: Theme) => ({
  padding: "12px 14px",
  borderRadius: "8px",
  backgroundColor: "rgba(148, 163, 184, 0.12)",
  border: `1px solid ${theme.palette.divider}`,
  fontFamily: "Red Hat Display",
  fontSize: "13px",
  fontWeight: 500,
  color: theme.palette.modalColors.colorLable,
  whiteSpace: "pre-wrap",
  lineHeight: 1.5,
});

export const justificationBoxStyles = (
  isCorrect: boolean,
): SxProps<Theme> => (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    mt: 0.5,
    p: 1.5,
    borderRadius: "8px",
    backgroundColor: isCorrect ? "#66EF64B2" : "#DD503E",
    color: isCorrect
      ? isDark
        ? "#FFFFFF"
        : theme.palette.modalColors.colorLable
      : "#FFFFFF",
    fontFamily: "Red Hat Display",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  };
};

export const answerListStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
};

export const itemTitleStyles: SxProps<Theme> = (theme: Theme) => ({
  fontFamily: "Red Hat Display",
  fontSize: "15px",
  fontWeight: 600,
  color: theme.palette.modalColors.colorLable,
});

export const itemMetaStyles: SxProps<Theme> = (theme: Theme) => ({
  fontFamily: "Red Hat Display",
  fontSize: "13px",
  fontWeight: 500,
  color: theme.palette.modalColors.colorLable,
  opacity: 0.75,
});

export const teamStatisticsButtonStyles: SxProps<Theme> = {
  mt: 0.75,
  alignSelf: "flex-start",
  borderRadius: "0.75rem",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "4px",
  padding: "6px 12px",
  fontSize: "12px",
  minHeight: "32px",
  textTransform: "none",
  fontFamily: "Red Hat Display",
  fontWeight: 600,
  backgroundColor: "#6FCF97",
  boxShadow: "none",
  color: "#FFFFFF",
  "&:hover": {
    boxShadow: "none",
    backgroundColor: "#6FCF97C0",
  },
  "&.Mui-disabled": {
    backgroundColor: "#6FCF97",
    color: "#FFFFFF",
    opacity: 0.7,
  },
};
